/**
 * SecurityTab Component
 * Active sessions management with revoke capabilities
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone, Globe, Loader2, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';
import { CollapsibleGlassCard } from '@/components/common/GlassCard';
import { Button } from '@/components/ui/button';
import { useSessions, useRevokeSession, useRevokeAllSessions } from '@/hooks/useSessions';
import { cn } from '@/lib/utils';

export function SecurityTab() {
  const { t } = useTranslation();
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [revokeSuccess, setRevokeSuccess] = useState(false);

  const { data: sessions, isLoading } = useSessions();
  const revokeSession = useRevokeSession();
  const revokeAll = useRevokeAllSessions();

  const handleRevokeSession = (id: string) => {
    revokeSession.mutate(id, {
      onSuccess: () => {
        setRevokeSuccess(true);
        setTimeout(() => setRevokeSuccess(false), 3000);
      },
    });
  };

  const handleRevokeAll = () => {
    revokeAll.mutate(undefined, {
      onSuccess: () => {
        setConfirmRevokeAll(false);
        setRevokeSuccess(true);
        setTimeout(() => setRevokeSuccess(false), 3000);
      },
    });
  };

  const getDeviceIcon = (session: { device?: string; userAgent?: string }) => {
    const d = (session.device || session.userAgent || '').toLowerCase();
    if (d.includes('mobile') || d.includes('phone') || d.includes('android') || d.includes('ios')) {
      return <Smartphone className="w-5 h-5" />;
    }
    return <Monitor className="w-5 h-5" />;
  };

  const getSessionLabel = (session: { browser?: string; device?: string; userAgent?: string }) => {
    return session.browser || session.device || session.userAgent || t('profile.security.unknownDevice');
  };

  const getLastActivity = (session: { lastActivity?: string; lastActivityAt?: string; createdAt: string }) => {
    const dateStr = session.lastActivity || session.lastActivityAt || session.createdAt;
    return new Date(dateStr).toLocaleString();
  };

  const otherSessions = sessions?.filter((s) => !s.isCurrent) ?? [];

  return (
    <div className="space-y-6">
      <CollapsibleGlassCard
        title={t('profile.security.title')}
        headerActions={
          otherSessions.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmRevokeAll(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
              disabled={revokeAll.isPending}
            >
              <Trash2 className="w-4 h-4" />
              {t('profile.security.revokeAll')}
            </Button>
          ) : undefined
        }
      >
          {revokeSuccess && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
              <CheckCircle className="w-4 h-4" />
              {t('profile.security.revokeSuccess')}
            </div>
          )}

          {/* Confirm revoke all dialog */}
          {confirmRevokeAll && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 mb-3">{t('profile.security.revokeAllConfirm')}</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmRevokeAll(false)}
                >
                  {t('profile.security.cancel')}
                </Button>
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleRevokeAll}
                  disabled={revokeAll.isPending}
                >
                  {revokeAll.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {t('profile.security.confirmRevoke')}
                </Button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : !sessions?.length ? (
            <p className="text-gray-500 text-sm py-4 text-center">
              {t('profile.security.noSessions')}
            </p>
          ) : (
            <div className="space-y-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className={cn(
                    'flex items-center justify-between p-4 rounded-xl border transition-colors',
                    session.isCurrent
                      ? 'border-[var(--color-primary-start)]/20 bg-gradient-to-r from-[var(--color-primary-start)]/5 to-transparent'
                      : 'border-gray-100 bg-white/60 hover:bg-white/80'
                  )}
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center',
                      session.isCurrent ? 'bg-[var(--color-primary-start)]/10 text-[var(--color-primary-start)]' : 'bg-gray-100 text-gray-500'
                    )}>
                      {getDeviceIcon(session)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-[var(--color-charcoal)]">
                          {getSessionLabel(session)}
                        </span>
                        {session.isCurrent && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-primary-start)]/10 text-[var(--color-primary-start)] font-medium">
                            {t('profile.security.currentSession')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        {session.ip && (
                          <span className="flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {session.ip}
                          </span>
                        )}
                        {session.location && <span>{session.location}</span>}
                        <span>
                          {t('profile.security.lastActive')}: {getLastActivity(session)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {!session.isCurrent && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeSession(session.id)}
                      disabled={revokeSession.isPending}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      {t('profile.security.revoke')}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {(revokeSession.isError || revokeAll.isError) && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              {t('profile.security.revokeError')}
            </div>
          )}
      </CollapsibleGlassCard>
    </div>
  );
}
